import {
  keepPreviousData,
  type QueryClient,
  queryOptions,
  useQuery,
  useSuspenseQuery,
} from '@tanstack/react-query';
import { ItemsApi } from '@/lib/api/common/items';
import type { ItemFiltersType } from '@/lib/api/types/itemFilters';
import type { ItemTreeSet } from '@/lib/api/types/items';
import { commonQueryKeys } from '@/lib/query-keys/common';

export const invalidateItemsDataSet = (queryClient: QueryClient) => {
  queryClient.invalidateQueries({ queryKey: commonQueryKeys.itemsDataSet.all() });
};

export const itemsDataSetQueryOptions = ({
  order,
  filter,
  enabled,
  keepPrevious = false,
}: {
  order?: string;
  filter?: ItemFiltersType;
  enabled?: boolean;
  keepPrevious?: boolean;
}) => {
  return queryOptions({
    queryKey: [...commonQueryKeys.itemsDataSet.byFilter(filter), order],
    queryFn: () => ItemsApi.getItemsDataSet({ filter, order }),
    enabled,
    placeholderData: keepPrevious ? keepPreviousData : undefined,
  });
};

export const useItemsDataSetQuery = ({
  filter,
  enabled,
  keepPrevious = false,
}: {
  filter?: ItemFiltersType;
  enabled?: boolean;
  keepPrevious?: boolean;
} = {}) => useQuery(itemsDataSetQueryOptions({ filter, enabled, keepPrevious }));

export const getItemsTreeSetQueryOptions = ({
  filter,
  order,
  includeDescendants,
}: {
  filter?: ItemFiltersType;
  order?: string;
  includeDescendants?: boolean;
} = {}) =>
  queryOptions({
    queryKey: [...commonQueryKeys.itemsTreeSet.byFilter(filter), order, includeDescendants],
    queryFn: () => ItemsApi.getItemsTreeSet({ filter, order, includeDescendants }),
    structuralSharing: false,
  });

export const useItemsTreeSetQuery = <TData = ItemTreeSet>({
  filter,
  order,
  enabled,
  keepPrevious = false,
  select,
  staleTime,
  refetchOnMount,
  includeDescendants,
}: {
  filter?: ItemFiltersType;
  order?: string;
  enabled?: boolean;
  keepPrevious?: boolean;
  select?: (data: ItemTreeSet) => TData;
  staleTime?: number;
  refetchOnMount?: boolean;
  includeDescendants?: boolean;
} = {}) =>
  useQuery({
    ...getItemsTreeSetQueryOptions({ filter, order, includeDescendants }),
    enabled,
    placeholderData: keepPrevious ? keepPreviousData : undefined,
    select,
    staleTime,
    refetchOnMount,
  });

export const useItemsTreeSetSuspenseQuery = ({
  filter,
  order,
  includeDescendants,
}: {
  filter?: ItemFiltersType;
  order?: string;
  includeDescendants?: boolean;
} = {}) => useSuspenseQuery(getItemsTreeSetQueryOptions({ filter, order, includeDescendants }));
